import { navigateAddModal, navigateRemoveModal, setNavigationPath } from 'client/actions/navigation'
import Button, { ButtonType } from 'client/components/common/Button/Button'
import DropSelect from 'client/components/common/DropSelect'
import { Modal } from 'client/components/common/Modal/Modal'
import { SelectInput } from 'client/components/common/SelectInput/SelectInput'
import { ToggleInput } from 'client/components/common/ToggleInput/ToggleInput'
import { PremiumFeatureType } from 'client/components/Modal/PremiumFeatureIntermediaryModal'
import { Spinner } from 'client/components/spinner/Spinner'
import bankConnectKeys from 'client/components/txtkeys/bankConnectKeys'
import { Analytics } from 'client/Global/Analytics'
import { PremiumModalOrigin, trackingFields } from 'client/Global/AnalyticsEvents'
import { Bank, RedirectUrl } from 'client/Interfaces/Banking'
import { QuotaTypes, SubscriptionInterface } from 'client/Interfaces/Subscription'
import { l } from 'client/Other/Translation'
import { useTypedSelector } from 'client/reducer'
import { doesCurrentPlanSupportFeature } from 'client/reducers/Subscription/companySubscription'
import * as React from 'react'
import { useDispatch } from 'react-redux'

import { createBankingUrl, fetchBanksByCountry } from '../bankingSlice'
import { warningMessageForUser } from '../helpers'
import './ConnectBankModal.scss'

export const MODAL_NAME = 'ConnectBankModal'

interface IActionProps {}

interface IStateProps {
  subscription: SubscriptionInterface
  dateFormat: string
  locale: string
}

interface IExtProps {
  isOpen: boolean
  redirectUrl: RedirectUrl
  onClose: () => void
}

export enum BankingSupportedCountries {
  be = 'be',
  de = 'de',
  fi = 'fi',
  fr = 'fr',
  gb = 'gb',
  nl = 'nl',
  se = 'se',
}

export enum BankingProviders {
  eb = 'eb',
}

export const ProvidersAndCountriesMap = {
  [BankingSupportedCountries.be]: [BankingProviders.eb],
  [BankingSupportedCountries.de]: [BankingProviders.eb],
  [BankingSupportedCountries.fi]: [BankingProviders.eb],
  [BankingSupportedCountries.fr]: [BankingProviders.eb],
  [BankingSupportedCountries.nl]: [BankingProviders.eb],
  [BankingSupportedCountries.se]: [BankingProviders.eb],
}

export const supportedCountriesMap = {
  [BankingSupportedCountries.be]: l(bankConnectKeys.bankConnect.modal.countryBelgium),
  [BankingSupportedCountries.de]: l(bankConnectKeys.bankConnect.modal.countryGermany),
  [BankingSupportedCountries.fi]: l(bankConnectKeys.bankConnect.modal.countryFinland),
  [BankingSupportedCountries.fr]: l(bankConnectKeys.bankConnect.modal.countryFrance),
  [BankingSupportedCountries.nl]: l(bankConnectKeys.bankConnect.modal.countryNetherlands),
  [BankingSupportedCountries.se]: l(bankConnectKeys.bankConnect.modal.countrySweden),
}

export const getAccountTypeOptionsMap = () => {
  return {
    business: l('ConnectBankModal.accountType.business'),
    personal: l('ConnectBankModal.accountType.personal'),
  }
}

export const getInactiveAccountType = (psuTypes) => {
  const allAccountTypes = Object.keys(getAccountTypeOptionsMap())
  return allAccountTypes.filter((type) => !psuTypes.includes(type))
}

export const showMultipleCountrySelection = (country: string): boolean => {
  switch (country) {
    case BankingSupportedCountries.be:
    case BankingSupportedCountries.de:
    case BankingSupportedCountries.fi:
    case BankingSupportedCountries.fr:
    case BankingSupportedCountries.nl:
    case BankingSupportedCountries.se:
      return false
    default:
      return true
  }
}

export type ConnectBankModalProps = IExtProps & IActionProps & IStateProps

export function ConnectBankModalComponent({ isOpen, onClose, redirectUrl }: ConnectBankModalProps): JSX.Element {
  const dispatch = useDispatch()
  const country = useTypedSelector((state) => state.company.country)
  const { bankingUrl, selectedBankId } = useTypedSelector((state) => state.banking)
  const { bank } = useTypedSelector((state) => state.bankWithAccounts)
  const { status: transactionStatus } = useTypedSelector((state) => state.bankTransactions)

  const [openBankSelect, setOpenBankSelect] = React.useState(false)
  const [highlightSelect, setHighlightSelect] = React.useState(true)
  const [searchText, setSearchText] = React.useState('')
  const [searchTextInvalid, setSearchTextInvalid] = React.useState(false)
  const [selectedCountry, setSelectedCountry] = React.useState<string>(country.toLowerCase())
  const [selectedBank, setSelectedBank] = React.useState<Bank | null>(null)
  const [selectedAccountType, setSelectedAccountType] = React.useState(null)

  React.useEffect(() => {
    dispatch(fetchBanksByCountry(selectedCountry))
    setSelectedBank(null)
  }, [dispatch, selectedCountry])

  const banks = useTypedSelector((state) => state.banking.banks)
  const bankingStatus = useTypedSelector((state) => state.banking.status)
  const subscription = useTypedSelector((state) => state.subscriptionManagement.companySubscription.subscription)

  React.useEffect(() => {
    if (transactionStatus === 'expired') {
      banks.map((listedBank) => {
        if (listedBank.name === bank.name) {
          setSelectedBank(listedBank)
        }
      })
    }
  }, [banks])

  React.useEffect(() => {
    const result = banks.filter((item) => item.name.toLowerCase().includes(searchText.toLowerCase()))

    if (searchText === '' || result.length) {
      setSearchTextInvalid(false)
    } else if (bankingStatus === 'succeed' && !result.length) {
      setSearchTextInvalid(true)
    }
  }, [bankingStatus, banks, searchText])

  const hasFeatureAccess = doesCurrentPlanSupportFeature(subscription, QuotaTypes.BANKING)
  const openSubscriptionModal = () => {
    dispatch(
      navigateAddModal({
        component: 'PremiumPlanModal',
        props: {
          isOpen: true,
          origin: PremiumModalOrigin.Banking,
          hideDowngradePlans: true,
          next: () => dispatch(setNavigationPath('/banking')),
        },
      }),
    )
  }

  const openPaymentWall = () => {
    dispatch(
      navigateAddModal({
        component: 'PremiumFeatureIntermediaryModal',
        props: {
          isOpen: true,
          featureType: PremiumFeatureType.BANKING,
          next: () => {
            dispatch(navigateRemoveModal('PremiumFeatureIntermediaryModal'))
            dispatch(openSubscriptionModal())
          },
        },
      }),
    )
  }

  const onBanklogoClick = (item) => {
    setSelectedBank(item)
    setOpenBankSelect(false)
  }

  const handleConnectBank = (redirectUrl: RedirectUrl) => {
    Analytics.trackEvent(trackingFields.name.connectBankModal, {
      type: trackingFields.type.action,
      category: trackingFields.category.bankConnection,
      result: trackingFields.Modifiers.success,
      placement: trackingFields.placement.bankConnection,
      bank: selectedBank.name,
      accountType: selectedAccountType,
    })
    if (hasFeatureAccess) {
      if (selectedBank) {
        if (selectedBank.psuTypes && selectedAccountType) {
          const EbRequest = {
            bankName: selectedBank.name,
            country: selectedBank.country,
            psuType: selectedAccountType,
            redirectUrl,
          }
          dispatch(createBankingUrl(EbRequest))
        }
      }
    } else if (!hasFeatureAccess && subscription.status === 'active') {
      openSubscriptionModal()
    } else {
      openPaymentWall()
    }
  }

  const closeConnectBankModal = () => {
    onClose()
    Analytics.trackEvent(trackingFields.name.connectBankModal, {
      type: trackingFields.type.action,
      category: trackingFields.category.bankConnection,
      result: trackingFields.Modifiers.cancel,
      placement: trackingFields.placement.bankConnection,
    })
  }

  const preselectedAccountType = () => {
    if (selectedBank.psuTypes.includes('personal') && selectedBank.psuTypes.includes('business')) {
      setSelectedAccountType('business')
    } else {
      setSelectedAccountType(selectedBank.psuTypes.toString())
    }
  }

  React.useEffect(() => {
    if (selectedBank) {
      if (bankingUrl) {
        localStorage.setItem('SELECTED_BANK_ID', selectedBankId)
        localStorage.setItem('SELECTED_BANK', JSON.stringify(selectedBank))
        localStorage.setItem('SELECTED_BANKING_PROVIDER', BankingProviders.eb)
        localStorage.setItem('SELECTED_ACCOUNT_TYPE', selectedAccountType)
        window.open(bankingUrl, '_top')
        onClose()
      }
      preselectedAccountType()
    }
  }, [bankingUrl, selectedBank, onClose])
  return (
    <Modal
      size="medium"
      isOpen={isOpen}
      header={l('ConnectBankModal.header.title')}
      className={MODAL_NAME}
      showCloseIcon={true}
      onClose={closeConnectBankModal}
      automation={MODAL_NAME}
      preventDrop={true}
    >
      <div className={`${MODAL_NAME}__title`}>{l('ConnectBankModal.title')}</div>
      {showMultipleCountrySelection(country.toLowerCase()) && (
        <SelectInput
          label={l('ConnectBankModal.selectCountry.label')}
          value={selectedCountry}
          map={supportedCountriesMap}
          automation={'customerType'}
          onChange={(country) => {
            setSelectedCountry(country)
          }}
        />
      )}

      {bankingStatus === 'fetchingBanksByCountry' || !banks.length ? (
        <Spinner />
      ) : (
        <>
          <div className={`${MODAL_NAME}__BanksDropSelect`}>
            <div className={`${MODAL_NAME}__BanksDropSelect__label`}>{l('ConnectBankModal.BanksDropSelect.label')}</div>
            <DropSelect<Bank>
              automation="banksSelector"
              className={`${MODAL_NAME}__BanksDropSelect__selector`}
              open={openBankSelect}
              onOpen={() => {
                Analytics.trackEvent(trackingFields.name.selectBank, {
                  type: trackingFields.type.action,
                  category: trackingFields.category.bankConnection,
                  result: trackingFields.Modifiers.open,
                  placement: trackingFields.placement.bankConnection,
                })
                setOpenBankSelect(true)
              }}
              onClose={() => {
                if (openBankSelect) {
                  setOpenBankSelect(false)
                }
              }}
              maxAllowed={{ limit: -1 }}
              highlight={highlightSelect}
              onClickOutside={() => {
                setHighlightSelect(false)
              }}
              hasError={searchTextInvalid && bankingStatus === 'failed'}
              noItemsFoundText={l('ConnectBankModal.dropSelect.noBanksFound')}
              onClick={() => {}}
              items={banks}
              selectedItem={selectedBank}
              getSelectedText={(b) => b.name}
              placeholder={l('ConnectBankModal.dropSelect.placeHolderText')}
              showSearchIcon={true}
              onSelect={(b) => {
                setSelectedBank(b)
                Analytics.trackEvent(trackingFields.name.selectBank, {
                  type: trackingFields.type.action,
                  category: trackingFields.category.bankConnection,
                  result: trackingFields.Modifiers.click,
                  placement: trackingFields.placement.bankConnection,
                  bank: b,
                })
              }}
              sortFn={(a, b) => a.name.localeCompare(b.name)}
              filterFn={(item, searchText) => {
                setSearchText(searchText)
                return (item.name || '').toLowerCase().includes((searchText || '').toLowerCase())
              }}
              renderLogo={(item, cls) => {
                const logoUrl = item.logo
                return logoUrl ? (
                  <img
                    className={cls('__Logo')}
                    src={`${item.logo}-/preview/200x24/-/setfill/ffffff00/-/crop/200x24/left/`}
                    alt={'Bank Logo Icon'}
                    onClick={() => onBanklogoClick(item)}
                  />
                ) : (
                  <div className={cls('__defaultLogo')} />
                )
              }}
              renderItem={(item) => item.name}
            />
          </div>

          {searchTextInvalid && (
            <div className={`${MODAL_NAME}__searchTextInvalid`}>
              {l('ConnectBankModal.searchTextInvalid.text')}
              <Button
                label={l('ConnectBankModal.searchTextInvalid.readMore')}
                type={ButtonType.link}
                action={() => {
                  window.open(l('ConnectBankModal.searchTextInvalid.readMore.link'), '_blank')
                }}
              />
            </div>
          )}
          {selectedBank && selectedBank.showContactBankInfo ? warningMessageForUser(selectedBank) : ''}
          {selectedBank && selectedBank.psuTypes && (
            <ToggleInput
              radio={true}
              label={l('ConnectBankModal.selectAccountType.label')}
              automation={'selectAccountType'}
              value={selectedAccountType}
              map={getAccountTypeOptionsMap()}
              onChange={(selectedType) => {
                setSelectedAccountType(selectedType)
              }}
              inactiveItems={selectedBank.psuTypes ? getInactiveAccountType(selectedBank.psuTypes) : []}
              labelHelpText={l('ConnectBankModal.selectAccountType.labelHelpText')}
            />
          )}

          <div className={`${MODAL_NAME}__description`}>
            <div className={`${MODAL_NAME}__description__title`}>{l('ConnectBankModal.description.title')}</div>
            <div className={`${MODAL_NAME}__description__body`}>
              <div className={`${MODAL_NAME}__description__body__text`}>{l('ConnectBankModal.description.body')}</div>
              <div className={`${MODAL_NAME}__description__body__logo`} />
            </div>
          </div>
        </>
      )}

      <footer>
        <Button
          label={l('ConnectBankModal.button.connectBank')}
          type={
            !selectedBank || searchTextInvalid || (selectedBank.psuTypes && !selectedAccountType)
              ? ButtonType.acceptDisabled
              : ButtonType.accept
          }
          disabled={!selectedBank || searchTextInvalid || (selectedBank.psuTypes && !selectedAccountType)}
          showLoadingAnimation={Boolean(bankingStatus === 'creatingBankingUrl')}
          action={() => handleConnectBank(redirectUrl)}
          automation={`${MODAL_NAME}-connect-bank`}
        />
        <Button
          label={l('Cancel')}
          type={ButtonType.cancel}
          action={closeConnectBankModal}
          automation={`${MODAL_NAME}-cancel`}
        />
      </footer>
    </Modal>
  )
}

export const ConnectBankModal = ConnectBankModalComponent
export default ConnectBankModal
